import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const HeroSliderClear = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Hero slider images - Crystal clear, high-quality medical images
  const heroSlides = [
    {
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=2400&q=95&auto=format&fit=crop",
      title: "Excellence in Respiratory Care",
      subtitle: "Advanced pulmonology treatments with compassionate care"
    },
    {
      image: "https://images.unsplash.com/photo-1538108149393-fbbd81895907?w=2400&q=95&auto=format&fit=crop",
      title: "Multi-Speciality Healthcare",
      subtitle: "Comprehensive medical services under one roof"
    },
    {
      image: "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=2400&q=95&auto=format&fit=crop",
      title: "State-of-the-Art Facilities",
      subtitle: "Modern equipment for accurate diagnosis and treatment"
    }
  ];

  // Auto-slide effect
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;800&family=Inter:wght@300;400;500;600;700;800&display=swap');

        /* ========== HERO SLIDER ========== */
        .hero-slider {
          position: relative;
          height: 100vh;
          margin-top: 0;
          overflow: hidden;
        }

        .slide {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          transition: opacity 1s ease-in-out;
        }

        .slide.active {
          opacity: 1;
        }

        .slide-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          /* Crystal clear image rendering */
          image-rendering: -webkit-optimize-contrast;
          image-rendering: crisp-edges;
          backface-visibility: hidden;
          transform: translateZ(0);
          /* Slight enhancement for medical images */
          filter: brightness(1.02) contrast(1.05);
        }

        /* Minimal overlay - only for text readability */
        .slide-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          /* Very subtle dark overlay only where text is */
          background: linear-gradient(to bottom, 
            rgba(0, 0, 0, 0) 0%, 
            rgba(0, 0, 0, 0.2) 40%,
            rgba(0, 0, 0, 0.3) 60%,
            rgba(0, 0, 0, 0) 100%
          );
        }

        .slide-content {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          text-align: center;
          color: white;
          width: 90%;
          max-width: 900px;
          z-index: 2;
        }

        .slide.active .slide-content {
          animation: slideUp 1s ease forwards;
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translate(-50%, -40%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        .slide-title {
          font-family: 'Playfair Display', serif;
          font-size: 4rem;
          font-weight: 800;
          margin-bottom: 1.5rem;
          line-height: 1.2;
          /* Enhanced text shadow for readability on clear images */
          text-shadow: 
            0 2px 10px rgba(0, 0, 0, 0.5),
            0 4px 20px rgba(0, 0, 0, 0.4),
            2px 2px 4px rgba(0, 0, 0, 0.6);
        }

        .slide-subtitle {
          font-size: 1.5rem;
          font-weight: 400;
          margin-bottom: 2.5rem;
          opacity: 0.98;
          text-shadow: 
            0 2px 8px rgba(0, 0, 0, 0.5),
            0 3px 15px rgba(0, 0, 0, 0.4);
        }

        .cta-button {
          padding: 1rem 2.5rem;
          background: linear-gradient(135deg, #E86A33, #FF9B6E);
          color: white;
          border: none;
          border-radius: 50px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 6px 20px rgba(232, 106, 51, 0.4);
        }

        .cta-button:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 30px rgba(232, 106, 51, 0.5);
        }

        .slider-controls {
          position: absolute;
          bottom: 4rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 1rem;
          z-index: 3;
        }

        .slider-btn {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.25);
          backdrop-filter: blur(10px);
          border: 2px solid rgba(255, 255, 255, 0.4);
          color: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s ease;
        }

        .slider-btn:hover {
          background: #E86A33;
          border-color: #E86A33;
          transform: scale(1.1);
        }

        .slider-dots {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 0.8rem;
          z-index: 3;
        }

        .dot {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.7);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .dot.active {
          background: #E86A33;
          border-color: #E86A33;
          width: 40px;
          border-radius: 10px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .slide-title {
            font-size: 2.5rem;
          }

          .slide-subtitle {
            font-size: 1.1rem;
          }

          .slider-controls {
            bottom: 6rem;
          }

          .slider-btn {
            width: 40px;
            height: 40px;
          }
        }
      `}</style>

      <section className="hero-slider" id="home">
        {heroSlides.map((slide, index) => (
          <div key={index} className={`slide ${index === currentSlide ? 'active' : ''}`}>
            <img 
              src={slide.image} 
              alt={slide.title} 
              className="slide-image"
              loading={index === 0 ? "eager" : "lazy"}
            />
            <div className="slide-overlay"></div>
            <div className="slide-content">
              <h1 className="slide-title">{slide.title}</h1>
              <p className="slide-subtitle">{slide.subtitle}</p>
              <button className="cta-button">
                Explore Services
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        ))}

        <div className="slider-controls">
          <button className="slider-btn" onClick={prevSlide} aria-label="Previous slide">
            <ChevronLeft size={24} />
          </button>
          <button className="slider-btn" onClick={nextSlide} aria-label="Next slide">
            <ChevronRight size={24} />
          </button>
        </div>

        <div className="slider-dots">
          {heroSlides.map((_, index) => (
            <div
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              role="button"
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </section>
    </>
  );
};

export default HeroSliderClear;